import { useCallback, useEffect, useState } from 'react';

import { useSetup } from '@/common/utils/hooks';
import {
  absolute,
  Button,
  Card,
  combineAnimations,
  errorBorder,
  errorShake,
  flexCenter,
  FlexColumn,
  flexColumn,
  fullSize,
  growShrink,
  Input,
  padding,
  Spacing,
  Splash,
  standardSizes,
} from '@toilet-paper-games/ui';

import { ToastIntent } from '@/common/components/base';
import { BlogLink } from '@/common/components/BlogLink';
import { replaceControllerCodeUrl } from '@/common/utils/url';
import { UserBanner } from '@/controller/components/UserBanner';
import { observer } from 'mobx-react-lite';
import {
  SessionStorageKeys,
  useControllerAppModel,
  useControllerNetworkHandler,
} from '../models/ControllerAppModel';

const useId = () => {
  const url = window.location.href;

  const path = '/controller/#/';

  if (!url.includes(path)) return null;

  const id = url.substring(url.indexOf(path) + path.length);

  return id;
};

export const EnterCode = observer(() => {
  const id = useId();

  const networkHandler = useControllerNetworkHandler();
  const appStore = useControllerAppModel();

  const [input, setInput] = useState(
    () =>
      id?.toUpperCase() ?? sessionStorage.getItem(SessionStorageKeys.HOSTER_UUID) ?? '',
  );
  const [rejected, setRejected] = useState(false);
  const [joining, setJoining] = useState(!!id);

  useSetup(() => {
    handleCodeChange();
  });

  const handleCodeChange = useCallback(async () => {
    setRejected(false);

    if (!input) return;

    try {
      setJoining(true);
      const connection = networkHandler.setupHosterConnection(input);
      if (!connection) return;

      await connection.catch((e) => {
        console.error(e);
        appStore.toastManager.addToast({
          title: String(e),
          duration: 7_500,
          intent: ToastIntent.ERROR,
        });
        throw e;
      });
      replaceControllerCodeUrl(input);
    } catch {
      setRejected(true);
      setJoining(false);
      return;
    } finally {
      setJoining(false);
    }
  }, [networkHandler, input]);

  const onFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleCodeChange();
    },
    [handleCodeChange],
  );

  useEffect(() => {
    if (!joining || rejected) return;
    const slowConnectionWarning = setTimeout(() => {
      if (rejected || !joining) return;
      appStore.toastManager.addToast({
        title: 'Things are taking a while, there may be an issue with peerjs',
        duration: 7_500,
        intent: ToastIntent.WARNING,
      });
    }, 7_500);

    return () => clearTimeout(slowConnectionWarning);
  }, [rejected, joining, appStore.toastManager]);

  // For the case of auto join from scanning qr code
  if (!rejected && joining && id && id.toLowerCase() === input.toLowerCase()) {
    return (
      <Splash>
        <Card css={[growShrink('2s')]}>Joining...</Card>
      </Splash>
    );
  }

  return (
    <Splash>
      {/* <Link css={[{ height: '10%' }, absolute(0)]} to={blogUrl} target="_blank">
        <img src={tpGameBannerPng} height={'100%'} />
      </Link> */}
      <FlexColumn css={[flexCenter]}>
        <UserBanner />

        <Spacing />
        <Card css={[padding('xl'), flexCenter]}>
          <form onSubmit={onFormSubmit} css={[flexColumn, flexCenter]}>
            <h4>Enter game code</h4>
            <Spacing />
            <Input
              type="text"
              onChange={(e) => {
                const element = e.target;
                const pointer = element.selectionStart;

                requestAnimationFrame(() => {
                  element.selectionStart = pointer;
                  element.selectionEnd = pointer;
                });

                setInput(e.target.value.toUpperCase());
              }}
              value={input}
              autoComplete="off"
              css={[fullSize, rejected && combineAnimations(errorShake(), errorBorder())]}
            />
            <Spacing />
            <Button disabled={!input || joining} type="submit">
              {joining ? 'Joining' : 'Join'}
            </Button>
          </form>
        </Card>
      </FlexColumn>
      <BlogLink css={absolute(undefined, undefined, standardSizes.md)} />
    </Splash>
  );
});
